import { useEffect } from 'react';
import { accessStorage } from '@chronosphereio/core';
import { PINNED_TIME_RANGE_KEY, PINNED_TIME_RANGE_ACTIVE_KEY } from '../../../plugin-system/runtime/TimeRangeProvider';
import { PinnedTimeRangeValue } from '@/core';

/**
 * Unpin the native dashboard time range when a tab is closed
 */
export const useUnpinTimeRange = () => {
  useEffect(() => {
    // We use the `beforeunload` event to decrement the `tabCount` when a tab is closed. However, this event is
    // also triggered on page refresh. To ensure we only decrement the tab count on tab close, not refresh,
    // we store a backup value. If pinning is active and the backup `tabCount` is greater than that in the pin value,
    // update the pin to match the backup. This code only runs on initial page load to ensure we're only applying
    // the backup on refresh.
    const { getItem: getPinnedTimeRange, setItem: setPinnedTimeRange } = accessStorage<PinnedTimeRangeValue>(
      window.localStorage,
      PINNED_TIME_RANGE_KEY
    );
    const pinnedTimeRange = getPinnedTimeRange();
    const pinnedTimeRangeActive = accessStorage(window.sessionStorage, PINNED_TIME_RANGE_ACTIVE_KEY).getItem();

    try {
      if (
        pinnedTimeRangeActive &&
        pinnedTimeRange &&
        pinnedTimeRange.backup &&
        pinnedTimeRange.backup.tabCount > pinnedTimeRange.tabCount
      ) {
        setPinnedTimeRange({
          value: pinnedTimeRange.backup.value,
          tabCount: pinnedTimeRange.tabCount + 1,
          backup: { value: pinnedTimeRange.backup.value, tabCount: pinnedTimeRange.tabCount + 1 },
        });
      }
    } catch (_error) {
      // noop
    }

    const handleBeforeUnload = () => {
      const { getItem: getPinnedTimeRange, setItem: setPinnedTimeRange } = accessStorage<PinnedTimeRangeValue>(
        window.localStorage,
        PINNED_TIME_RANGE_KEY
      );
      const pinnedTimeRange = getPinnedTimeRange();
      const pinnedTimeRangeActive = accessStorage(window.sessionStorage, PINNED_TIME_RANGE_ACTIVE_KEY).getItem();

      try {
        if (pinnedTimeRange && pinnedTimeRange?.tabCount > 0 && pinnedTimeRangeActive) {
          // decrement `tabCount` and do not update backup value
          setPinnedTimeRange({
            ...pinnedTimeRange,
            tabCount: Math.max(pinnedTimeRange.tabCount - 1, 0), // don't let count go below 0
          });
        }
      } catch (_error) {
        // noop
      }
    };

    // add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // cleanup function to remove event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // We only want to call this code on initial page load so use an empty array for the dependency array
  }, []);
};
